import type { Activity, ItineraryDay } from '../types';

/**
 * The itinerary as it looks once `ids` are deleted — the local twin of the
 * cascade in `server/src/modules/itinerary/itinerary.service.ts` deleteActivity.
 *
 * Deleting an origin card turns its day's "continues from" off. Deleting a
 * day's last real stop makes the next day's origin card stale, and the server
 * drops it, so the copy here drops it too.
 *
 * Pure on purpose: a deferred delete (ADR-0015) applies it when it hides the
 * cards, and again if a refetch brings them back before the DELETE is sent.
 */
export function removeActivitiesLocally(days: ItineraryDay[], ids: string[]): ItineraryDay[] {
  const removing = new Set(ids);
  const dropNextOrigin = new Set<number>();

  let updated = days.map(day => {
    if (!day.activities.some(a => removing.has(a.id))) return day;
    const lastStop = day.activities
      .filter(a => !a.isOrigin)
      .reduce<Activity | null>((last, a) => (!last || a.sortOrder > last.sortOrder ? a : last), null);
    if (lastStop && removing.has(lastStop.id)) dropNextOrigin.add(day.dayNumber + 1);
    const originRemoved = day.activities.some(a => a.isOrigin && removing.has(a.id));
    return {
      ...day,
      activities: day.activities.filter(a => !removing.has(a.id)),
      startFromPrevStop: originRemoved ? false : day.startFromPrevStop,
    };
  });

  if (dropNextOrigin.size > 0) {
    updated = updated.map(day =>
      dropNextOrigin.has(day.dayNumber) && day.startFromPrevStop
        ? { ...day, activities: day.activities.filter(a => !a.isOrigin), startFromPrevStop: false }
        : day,
    );
  }
  return updated;
}
